import { defineStore } from 'pinia'
import { useQuery } from '@tanstack/vue-query'
import { z } from 'zod'
import { noAuthApi } from '@dashboard/services/axiosApi'
import type { Template } from '@dashboard/types/template'
import { computed, ref } from 'vue'

const templateEntrySchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().nullish(),
  preview_image: z.object({ url: z.string().optional() }).nullish(),
  template_type: z.object({ value: z.string().nullish() }).nullish(),
  hide_preview: z.boolean().optional(),
  impact_statement: z.string().nullish(),
  best_for: z.string().nullish(),
  template_categories: z.array(z.union([z.string(), z.object({ slug: z.string() })])).nullish(),
  is_skeleton: z.boolean().optional(),
  payload_json: z.object({ value: z.string() }).nullish()
})

const templateListResponseSchema = z.object({
  data: z.array(templateEntrySchema),
  meta: z.object({ last_page: z.number().optional() }).optional()
})

const templateDetailResponseSchema = z.object({ data: templateEntrySchema })

type TemplateEntry = z.infer<typeof templateEntrySchema>
type StoredTemplate = Template & { hidePreview?: boolean; json: string }

const transformTemplate = (template: TemplateEntry): StoredTemplate | undefined => {
  const json = template.payload_json?.value

  if (!json) {
    return
  }

  try {
    return {
      id: template.id,
      title: template.title,
      description: template.description || '',
      type: template.template_type?.value || '',
      previewImage: template.preview_image?.url,
      hidePreview: template.hide_preview,
      impactStatement: template.impact_statement ?? undefined,
      bestFor: template.best_for ?? undefined,
      templateCategories:
        template.template_categories?.map((category) => (typeof category === 'string' ? category : category.slug)) ||
        [],
      templateDetails: {
        json: JSON.parse(json)
      },
      json
    }
  } catch {
    return
  }
}

export const useTemplateStore = defineStore('template', () => {
  const entriesEndpoint = '/api/template-catalog'
  const fetchedTemplates = ref<Record<string, StoredTemplate>>({})

  const templatesQuery = useQuery({
    queryKey: ['templates'],
    queryFn: async () => {
      const response = await noAuthApi.get(entriesEndpoint)

      return templateListResponseSchema.parse(response.data)
    },
    retry: false
  })

  const templates = computed(() => templatesQuery.data.value?.data || [])

  const transformedTemplates = computed(() =>
    templates.value.filter((template) => !template.is_skeleton).flatMap((template) => transformTemplate(template) || [])
  )

  const fetchById = async (id: string) => {
    const response = await noAuthApi.get(`${entriesEndpoint}/${id}`)
    const template = transformTemplate(templateDetailResponseSchema.parse(response.data).data)

    if (template) {
      fetchedTemplates.value = { ...fetchedTemplates.value, [id]: template }
    }

    return template
  }

  const getById = (id: string) =>
    fetchedTemplates.value[id] || transformedTemplates.value.find((template) => template.id === id)

  return {
    templates,
    transformedTemplates,
    fetchById,
    getById
  }
})
